import { Link } from 'react-router-dom';
import { Building2, CalendarCheck, Inbox, Loader2, ClipboardCheck, AlertCircle } from 'lucide-react';
import { PropertyReviewStatus } from '@pms/shared';
import { usePlatformOverview } from '@/hooks/api/usePlatform';
import { StatCard } from '@/components/ui/StatCard';
import { PageHeader } from '@/components/ui/PageHeader';

const REVIEW_STATUS_META: Record<PropertyReviewStatus, { label: string; color: string }> = {
  [PropertyReviewStatus.DRAFT]: { label: 'Draft', color: 'text-gray-500' },
  [PropertyReviewStatus.PENDING_REVIEW]: { label: 'Pending Review', color: 'text-amber-600' },
  [PropertyReviewStatus.CHANGES_REQUESTED]: { label: 'Changes Requested', color: 'text-orange-600' },
  [PropertyReviewStatus.APPROVED]: { label: 'Approved', color: 'text-green-600' },
};

export function AdminOverviewPage() {
  const { data, isLoading, isError, refetch } = usePlatformOverview();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 size={28} className="animate-spin text-brand-500" aria-hidden="true" />
        <span className="ml-2 text-sm text-gray-500">Loading overview…</span>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-8 text-center">
        <AlertCircle size={28} className="mx-auto text-red-500 mb-3" aria-hidden="true" />
        <p className="text-sm text-red-700 mb-4">Unable to load platform overview.</p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  const byStatus = data.propertiesByReviewStatus;
  const pending = byStatus[PropertyReviewStatus.PENDING_REVIEW] ?? 0;

  return (
    <div className="space-y-8">
      <PageHeader
        title="Overview"
        description="Your BestAcco marketplace at a glance."
      />

      {/* Headline stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard icon={Building2} value={data.properties} label="Properties" borderColor="border-l-brand-500" />
        <StatCard icon={CalendarCheck} value={data.bookings} label="Bookings" borderColor="border-l-green-500" />
        <Link
          to="/admin/leads"
          className="rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          aria-label={`Owner leads: ${data.leads}. View owner leads.`}
        >
          <StatCard
            icon={Inbox}
            value={data.leads}
            label="Owner leads"
            borderColor="border-l-purple-500"
            className="h-full transition-shadow hover:shadow-md"
          />
        </Link>
      </div>

      {/* Review-status breakdown */}
      <section aria-labelledby="review-breakdown-heading">
        <div className="flex items-center justify-between mb-3">
          <h2 id="review-breakdown-heading" className="text-base font-semibold text-gray-900">
            Properties by review status
          </h2>
          <Link
            to="/admin/reviews"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:text-brand-700"
          >
            <ClipboardCheck size={15} aria-hidden="true" />
            Review queue
            {pending > 0 && (
              <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-100 text-[11px] font-semibold text-amber-800">
                {pending}
              </span>
            )}
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.values(PropertyReviewStatus).map((status) => {
            const meta = REVIEW_STATUS_META[status];
            return (
              <div
                key={status}
                className="bg-white rounded-xl border border-gray-200 shadow-sm p-5"
              >
                <p className={`text-3xl font-bold leading-none ${meta.color}`}>
                  {byStatus[status] ?? 0}
                </p>
                <p className="mt-2 text-sm text-gray-500">{meta.label}</p>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
